import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Outermost, defense-in-depth error boundary. Catches any render-time crash
 * anywhere in the tree (e.g. a downstream assumption about a `user`/session
 * shape that turns out to be undefined) and shows a real recovery UI
 * instead of an uncaught exception / blank page. Must be a class component
 * — React error boundaries have no hook equivalent.
 *
 * Deliberately does not log the caught error anywhere (console or
 * otherwise): the error/component stack can incidentally contain
 * clinical/user data rendered on screen, and this app treats logging as a
 * PHI-safety concern (see `src/test/consistency/phi/phi-safety.test.ts`).
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo): void {
    // Intentionally empty — see class doc comment.
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-canvas p-4">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Something went wrong</CardTitle>
            <CardDescription>
              An unexpected error occurred. Reloading the page usually fixes it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={this.handleReload}>Reload</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
}
